import {getEnv} from '../../common/env/env';
import {getCommonBadge} from '../../common/getCommonBadge';
import {BannerItemDto} from '../../common/shared/api/gateway/BannerItemDto.interface';
import {ViewTypeVo} from '../../common/shared/api/karina/vo/enums/ViewTypeVo.enum';
import {getDiscountRateText} from '../../common/shared/utils/getDiscountRateText';
import {getFormattedNumberWithSuffix} from '../../common/shared/utils/getFormattedNumberWithSuffix';
import {getKageUrl} from '../../common/shared/utils/getKageUrl';
import {isNilEmpty} from '../../common/shared/utils/isNilEmpty';
import {removeCategoryName} from '../../common/shared/utils/removeCategoryName';
import {
  BannerItem,
  BannerType,
  BannerViewType,
  ItemType,
} from '../generated/types';

export function createBannerItem({
  item,
  itemType,
  viewType,
}: {
  item: BannerItemDto;
  itemType: ItemType;
  viewType: BannerViewType;
}): BannerItem {
  const {
    category,
    sub_category,
    title,
    alt_text,
    scheme,
    thumbnail,
    video_url,
    caption,
    badge,
    service_property,
    is_waitfree,
    business_model,
    operator_property,
    category_uid,
    series_property,
    asset_property,
  } = item;
  const {is_event, is_view_count, cash_info, helix_imp_id} =
    operator_property || {};
  const {
    series_id: seriesId,
    title: seriesName,
    waitfree_period_by_minute,
  } = series_property || {};
  const {badgeList, statusBadge} = getCommonBadge({
    itemType: is_event ? ItemType.EventBanner : ItemType.Banner,
    badge,
    is_waitfree,
    waitfree_period_by_minute: waitfree_period_by_minute || null,
    business_model,
  });
  const {banner_set} = asset_property || {};
  const useBannerSetAssets =
    !isNilEmpty(banner_set) &&
    !isNilEmpty(banner_set.background_img) &&
    !isNilEmpty(banner_set.main_img);
  const useBannerSetTitleImage =
    !isNilEmpty(banner_set) && !isNilEmpty(banner_set.text_img);

  return {
    // 썸네일과 비디오는 동일할 수 있으므로 banner uid를 우선 활용합니다.
    id: `banner-${
      operator_property?.banner_uid ||
      series_property?.series_id ||
      thumbnail ||
      video_url
    }-${viewType}`,
    type: itemType,
    bannerType: video_url ? BannerType.Video : BannerType.Image,
    bannerViewType: viewType,
    title,
    altText: alt_text,
    badgeList,
    statusBadge,
    metaList: [
      ...(category !== undefined ? [category] : []),
      ...(sub_category !== undefined && category !== sub_category
        ? [removeCategoryName(sub_category, category_uid)]
        : []),
      ...(is_view_count
        ? [getFormattedNumberWithSuffix(service_property?.view_count || 0)]
        : []),
    ],
    scheme,
    videoUrl: video_url,
    thumbnail: useBannerSetAssets
      ? getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: banner_set.background_img as string,
          resolution: 'th3',
        })
      : // video 배너의 경우 thumbnail 이 존재하지 않을 수 있습니다. 이 경우 빈 스트링을 넣어줍니다
        thumbnail === undefined
        ? ''
        : thumbnail.startsWith('http')
          ? thumbnail
          : getKageUrl({
              kageUrlPrefix: getEnv().kageUrlPrefix,
              kageId: thumbnail,
              resolution: 'th3',
            }),
    caption,
    titleImage: useBannerSetTitleImage
      ? getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: banner_set.text_img as string,
        })
      : asset_property?.card_text_img
        ? getKageUrl({
            kageUrlPrefix: getEnv().kageUrlPrefix,
            kageId: asset_property?.card_text_img,
          })
        : undefined,
    seriesId: series_property?.series_id,
    backgroundColor: useBannerSetAssets
      ? banner_set?.background_color
      : undefined,
    characterImage: useBannerSetAssets
      ? getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: banner_set?.main_img as string,
          resolution: 'th3',
        })
      : undefined,
    eventLog: {
      eventMeta: {
        id: String(seriesId || scheme),
        type: seriesId ? 'seriesId' : 'scheme',
        name: title,
        category,
        subcategory: sub_category,
        provider: helix_imp_id ? 'helix' : '서비스',
        series: seriesName,
        series_id: seriesId ? String(seriesId) : undefined,
      },
      click: {
        imp_provider: helix_imp_id ? 'helix' : 'service',
      },
      customProps: {
        event_series_id: seriesId ? String(seriesId) : undefined,
        play_url: video_url,
        view_type: ORIGIN_VIEW_TYPE_MAP[viewType],
        landing_path: scheme,
        banner_uid:
          operator_property?.banner_uid !== undefined
            ? String(operator_property?.banner_uid)
            : undefined,
        ...(helix_imp_id ? {helix_id: helix_imp_id, helix_yn: 'y'} : {}),
      },
      viewimp_contents: {
        imp_provider: helix_imp_id ? 'helix' : 'service',
        imp_type: 'vimp',
        id: seriesId ? String(seriesId) : scheme,
        type: seriesId ? 'seriesId' : 'scheme',
        name: title,
      },
    },
    discountRate: cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: cash_info?.discount_rate,
      setDiscountRate: cash_info?.set_discount_rate,
    }),
  };
}

const ORIGIN_VIEW_TYPE_MAP: {
  [key in BannerViewType]: ViewTypeVo | undefined;
} = {
  [BannerViewType.Rectangle]: ViewTypeVo.RectangleView,
  [BannerViewType.Square]: ViewTypeVo.SquareView,
  [BannerViewType.Unknown]: undefined,
};
